import type { WsAccount } from "@storyboard/web-client";
import type { UserInfoResponse } from "@storyboard/web-client/auth";
import { createLogger } from "@/logger";

const log = createLogger("query");

import { and, coalesce, eq, type InitialQueryBuilder, isUndefined } from "@tanstack/db";

import {
  membershipIsDriving,
  membershipIsHandsFreeOff,
  membershipIsNotDriving,
  membershipIsOffline,
  membershipIsVolumeMuted,
} from "@/store/availabilityPredicates";
import { chainComparators, compareAsc, compareDesc } from "@/store/compare";
import {
  availabilityForMembership,
  feedHasUnread,
  firstAliasForFeed,
  formatLocation,
  groupForFeed,
  latestItemForFeed,
  preferenceForFeed,
  transcriptionForContent,
} from "@/store/feedSelectors";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Account } from "@/store/types";
import type { DriverSortKey, DriverViewTab } from "@/types/driver";

export type { UserInfoResponse };

export async function upsertMyAccount(userInfo: UserInfoResponse) {
  const accountId = userInfo?.session?.credentials?.[0].CredentialScopes?.[0].accountId;

  if (!accountId) {
    log.error("No account id found in userInfo", userInfo);
    return;
  }

  const incoming: Account = {
    id: accountId,
    mine: true,
    preferredLanguage: null,
    name: userInfo?.name ?? null,
    accountType: userInfo?.accountType ?? null,
    avatarColor: userInfo?.avatarColor ?? null,
    email: userInfo?.email ?? null,
    firstName: null,
    lastName: null,
    phoneNumber: null,
    emailVerified: false,
    phoneNumberVerified: false,
    bot: userInfo?.name === "Storyboard",
  };

  return store.upsert("account", [incoming], (existing, next) => ({
    ...existing,
    mine: true,
    name: next.name,
    accountType: next.accountType,
    avatarColor: next.avatarColor,
    email: next.email,
  }));
}

export async function bulkUpsertAccount(records: WsAccount[]) {
  if (records.length === 0) return;
  const data = records.map(
    (accountRecord): Account => ({
      id: accountRecord.id,
      name: accountRecord.name || null,
      accountType: accountRecord.accountType || null,
      avatarColor: accountRecord.avatarColor || null,
      firstName: accountRecord.firstName || null,
      lastName: accountRecord.lastName || null,
      email: accountRecord.email || null,
      emailVerified: accountRecord.emailVerified,
      phoneNumber: accountRecord.phoneNumber || null,
      phoneNumberVerified: accountRecord.phoneNumberVerified,
      bot: accountRecord?.name === "Storyboard",
      mine: false,
      preferredLanguage: null,
    }),
  );
  // The old ON CONFLICT SET replaced everything except the client-owned `mine` and
  // `preferredLanguage` flags.
  store.upsert("account", data, (existing, next) => ({
    ...next,
    mine: existing.mine,
    preferredLanguage: existing.preferredLanguage,
  }));
}

export async function setPreferredLanguage(accountId: string, language: string): Promise<void> {
  store.update("account", accountId, (row) => ({ ...row, preferredLanguage: language }));
}

export type AccountWithNoAvailabilityRow = {
  feedId: string | null;
  workspaceId: string;
  membershipStatus: "active" | "inactive";
  contact: string | null;
  role: "limitedMember";
  date: string;
  title: string | null;
  isAliasFeed: boolean;
  aliasMembershipId: string | null;
  accountId: string;
};

export const accountsWithNoAvailabilitiesQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ membership: store.collection("workspaceMembership") })
    .leftJoin({ availability: store.collection("wsMembershipAvailability") }, ({ membership, availability }) =>
      eq(membership.id, availability.workspaceMembershipId),
    )
    .innerJoin({ account: store.collection("account") }, ({ membership, account }) =>
      eq(membership.accountId, account.id),
    )
    .innerJoin({ alias: store.collection("workspaceCommandAlias") }, ({ membership, alias }) =>
      eq(membership.id, alias.workspaceMembershipId),
    )
    .innerJoin({ feed: store.collection("feed") }, ({ alias, feed }) => eq(alias.feedId, feed.id))
    .where(({ membership, availability }) =>
      and(
        eq(membership.workspaceId, workspaceId),
        eq(membership.status, "active"),
        eq(membership.role, "limitedMember"),
        isUndefined(availability.workspaceMembershipId),
      ),
    )
    .select(({ membership, account, alias, feed }) => ({
      feedId: alias.feedId,
      workspaceId: membership.workspaceId,
      membershipStatus: membership.status,
      contact: coalesce(account.name, account.email, account.phoneNumber),
      role: membership.role,
      date: alias.createdAt,
      title: feed.title,
      isAliasFeed: feed.isAliasFeed,
      aliasMembershipId: feed.aliasMembershipId,
      accountId: account.id,
    }));

export const lookupAccountById = async (accountId: string): Promise<WsAccount | null> => {
  if (!accountId) {
    return null;
  }
  return store.get("account", accountId) ?? null;
};

export type AccountWithWritePermissionRow = {
  accountId: string;
  driver: string | null;
  workspaceId: string | null;
  feedId: string;
  recentMessage: string | null;
  alias: string | null;
  unread: boolean | null;
  status: string | null;
  location: string | null;
  volume: number | null;
  country: string | null;
  stateProvince: string | null;
  feedPrefId: string | null;
  group: string | null;
  groupId: string | null;
  calculatedAvailability: string | null;
  offline: boolean | null;
  mostRecentHandsFreeStatusEnabled: boolean | null;
  mostRecentHandsFreeStatusUpdatedAt: string | null;
  mostRecentVolumeAudible: boolean | null;
  mostRecentDrivingStatus: string | null;
  mostRecentDutyStatusStatus: string | null;
  mostRecentDutyStatusChangedAt: string | null;
  mostRecentDrivingStatusChangedAt: string | null;
  latestItemCreatedAt: string | null;
};

type AccountsWhereIHaveWritePermissionParams = {
  workspaceId: string;
  membershipId: string;
  myCurrentWorkspaceMembershipId: string;
  type: DriverViewTab;
  role: string;
  groupFilterId: null | string[];
  channelSort: DriverSortKey;
  statusFilter?: string[];
  searchQuery?: string;
  language?: string;
};

function statusFilterMatchesLegacy(filter: string, availability: ReturnType<typeof availabilityForMembership>) {
  switch (filter) {
    case "driving":
      return membershipIsDriving(availability);
    case "not-driving":
      return membershipIsNotDriving(availability);
    case "hands-free-off":
      return membershipIsHandsFreeOff(availability);
    case "volume-muted":
      return membershipIsVolumeMuted(availability);
    case "offline":
      return membershipIsOffline(availability);
    case "never-logged-in":
      // Mirrors the old (acknowledged-incorrect) SQL bucket: no availability row synced yet.
      return availability === null;
    default:
      return false;
  }
}

export const accountsWhereIHaveWritePermissionCollections = [
  "account",
  "workspaceMembership",
  "feed",
  "permission",
  "workspaceCommandAlias",
  "item",
  "transcription",
  "wsMembershipAvailability",
  "feedGroupMembership",
  "feedGroup",
  "feedPreference",
] as const;

/** Stays a compute pipeline: search, status buckets, and per-feed latest-item aggregates. */
export function computeAccountsWhereIHaveWritePermission(
  params: AccountsWhereIHaveWritePermissionParams,
): AccountWithWritePermissionRow[] {
  {
    const {
      workspaceId,
      membershipId,
      myCurrentWorkspaceMembershipId,
      type,
      role,
      groupFilterId,
      channelSort,
      statusFilter,
      searchQuery,
      language = "en",
    } = params;
    const search = searchQuery && searchQuery.length > 0 ? searchQuery.toLowerCase() : null;

    const byAccount = new Map<string, AccountWithWritePermissionRow>();
    for (const membership of rowsByIndex("workspaceMembership", idx.membershipsByWorkspace, workspaceId)) {
      if (membership.status !== "active" || membership.accountId == null) continue;
      const account = store.get("account", membership.accountId);
      if (!account || byAccount.has(account.id)) continue;
      const availability = availabilityForMembership(membership.id);

      for (const feed of rowsByIndex("feed", idx.feedsByAliasMembership, membership.id)) {
        if (!feed.isAliasFeed) continue;
        const alias = firstAliasForFeed(feed.id);
        if (alias?.alias !== role) continue;
        const hasWrite = rowsByIndex("permission", idx.permissionsByFeed, feed.id).some(
          (p) => p.workspaceMembershipId === membershipId && p.name === "write" && p.enabled,
        );
        if (!hasWrite) continue;

        const group = groupForFeed(feed.id);
        const preference = preferenceForFeed(feed.id, myCurrentWorkspaceMembershipId, { notifyingOnly: true });
        if (type === "my" && preference === null) continue;
        if (groupFilterId && groupFilterId.length > 0 && (group === null || !groupFilterId.includes(group.id))) {
          continue;
        }
        if (
          search !== null &&
          !(
            feed.title?.toLowerCase().includes(search) ||
            group?.name?.toLowerCase().includes(search) ||
            account.name?.toLowerCase().includes(search)
          )
        ) {
          continue;
        }
        if (
          statusFilter &&
          statusFilter.length > 0 &&
          !statusFilter.some((s) => statusFilterMatchesLegacy(s, availability))
        ) {
          continue;
        }

        const latestItem = latestItemForFeed(feed.id);
        const transcription = transcriptionForContent(latestItem?.contentId, language);
        byAccount.set(account.id, {
          accountId: account.id,
          driver: account.name,
          workspaceId: feed.workspaceId,
          feedId: feed.id,
          recentMessage: transcription?.transcriptionContent ?? null,
          alias: alias?.alias ?? null,
          unread: feedHasUnread(feed.id),
          status: availability?.mostRecentDutyStatusStatus ?? null,
          location: formatLocation(availability),
          volume: availability?.mostRecentVolumeVolume ?? null,
          country: availability?.mostRecentDeviceLocationNearestAddressCountry ?? null,
          stateProvince: availability?.mostRecentDeviceLocationNearestAddressRegion ?? null,
          feedPrefId: preference?.id ?? null,
          group: group?.name ?? null,
          groupId: group?.id ?? null,
          calculatedAvailability: availability?.calculatedAvailability ?? null,
          offline: availability?.offline ?? null,
          mostRecentHandsFreeStatusEnabled: availability?.mostRecentHandsFreeStatusEnabled ?? null,
          mostRecentHandsFreeStatusUpdatedAt: availability?.mostRecentHandsFreeStatusUpdatedAt ?? null,
          mostRecentVolumeAudible: availability?.mostRecentVolumeAudible ?? null,
          mostRecentDrivingStatus: availability?.mostRecentDrivingStatus ?? null,
          mostRecentDutyStatusStatus: availability?.mostRecentDutyStatusStatus ?? null,
          mostRecentDutyStatusChangedAt: availability?.mostRecentDutyStatusChangedAt ?? null,
          mostRecentDrivingStatusChangedAt: availability?.mostRecentDrivingStatusChangedAt ?? null,
          latestItemCreatedAt: latestItem?.createdAt ?? null,
        });
        break;
      }
    }

    const rows = [...byAccount.values()];
    const byName =
      (direction: typeof compareAsc) => (a: AccountWithWritePermissionRow, b: AccountWithWritePermissionRow) =>
        direction(a.driver, b.driver);
    const byLatest = (a: AccountWithWritePermissionRow, b: AccountWithWritePermissionRow) =>
      compareDesc(a.latestItemCreatedAt, b.latestItemCreatedAt);
    const byId = (a: AccountWithWritePermissionRow, b: AccountWithWritePermissionRow) =>
      compareAsc(a.accountId, b.accountId);
    switch (channelSort) {
      case "alphaAsc":
        rows.sort(chainComparators(byName(compareAsc), byId));
        break;
      case "alphaDesc":
        rows.sort(chainComparators(byName(compareDesc), byId));
        break;
      case "most-recent":
        rows.sort(chainComparators(byLatest, byId));
        break;
      default:
        rows.sort(chainComparators((a, b) => compareDesc(a.unread, b.unread), byLatest, byId));
        break;
    }
    return rows;
  }
}
